import { Checkbox, Typography } from "@material-tailwind/react";
import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Fondy from "../components/Fondy";
import Monobank from "../components/Monobank";
import { ENABLE_INVESTING_ACCOUNT_CONTROLLER, ENABLE_UNIFIED_UPLOAD, USED_BANK } from "../constants/constants";
import {
  useTaxDeclarationsCalculatePreliminaryMutation,
  useTaxDeclarationsPaymentsMutation,
} from "../store";
import ImageShow from "./ImageShow";
import Link from "./Link";
import { env } from "../constants/env-config";
import { UNIFIED_UPLOAD_PATH } from "../pages/UnifiedUploadPage";

// AC-40: the declaration is refused for an unfinished year or from a report ending before 31 December (400 / code 123).
const FULL_YEAR_REPORT_REQUIRED = "tax_declaration.full_year_report_required";
const isFullYearReportRequired = (error) =>
  error?.status === 400 && (error.data?.code === FULL_YEAR_REPORT_REQUIRED || error.data?.systemCode === "123");
// Where a report is uploaded, as the sidebar's «Рахунки» leads (read at render: the flags are not import-time facts).
const uploadPath = () => (ENABLE_UNIFIED_UPLOAD && ENABLE_INVESTING_ACCOUNT_CONTROLLER ? UNIFIED_UPLOAD_PATH : "/accounts");

function Checkout({ year }) {
  const { t } = useTranslation();
  const bank = USED_BANK;
  const [url, setUrl] = useState("");
  const [calculatePreliminary, calculatePreliminaryData] =
    useTaxDeclarationsCalculatePreliminaryMutation();
  const [getPayment, paymentData] = useTaxDeclarationsPaymentsMutation();
  const [bonusChecked, setBonusChecked] = useState(false);

  const handleBonus = useCallback(() => {
    setBonusChecked((bonusChecked) => !bonusChecked);
  }, []);

  useEffect(() => {
    calculatePreliminary({ year, bonusChecked });
  }, [calculatePreliminary, year, bonusChecked]);

  // One payment per preliminary calculation: a re-render (a silent token renew among them) never posts it again and
  // never re-creates the Monobank iframe (B2-02).
  const paidCalculation = useRef(null);
  useEffect(() => {
    const id = calculatePreliminaryData?.data?.id;
    const calculation = calculatePreliminaryData?.requestId;
    if (id && paidCalculation.current !== calculation) {
      paidCalculation.current = calculation;
      getPayment({ id, bonusChecked, bank });
    }
  }, [calculatePreliminaryData, getPayment, bonusChecked, bank]);

  useEffect(() => {
    if (paymentData?.data?.checkoutUrl) {
      setUrl(paymentData?.data?.checkoutUrl);
    }
  }, [paymentData]);

  function formatNumbers(x) {
    return x
      .toFixed(2)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  }

  let content;
  if (calculatePreliminaryData.status === "fulfilled") {
    const data = calculatePreliminaryData?.data;
    const bonus = data.availableReferralBonuses;
    
    // Use new API fields with fallback to deprecated fields for backward compatibility
    const tradingProfit = data.tradingProfit ?? data.profit ?? 0;
    const dividendsIncome = data.dividendsIncome ?? 0;
    const otherIncome = data.otherIncome ?? 0;
    const totalPdfo = data.totalPdfo ?? data.pdfo ?? 0;
    const totalMilitaryTax = data.totalMilitaryTax ?? data.militaryTax ?? 0;
    
    content = (
      <div className="rounded-[30px] text-black bg-gradient-to-r from-cyan-650 to-cyan-75">
        <div className="px-8 py-7 text-black flex flex-col rounded-[30px] bg-white">
          {/* Block 1: Transactions */}
          <div className="pb-0 flex flex-wrap border-b border-gray-75">
            <span className="text-sm">{t('checkout.uniqueTrades')}</span>
            <span className="ml-auto">{data.tradesCount}</span>
            <Link
              className="relative -top-[7px] -right-[2px] h-5"
              handleClick={(e) => e.preventDefault}>
              <ImageShow image={{ url: "question", label: "question" }} />
              <div className="relative -top-4 h-[12px] justify-center opacity-0 hover:opacity-100 w-[12px]">
                <p className="absolute -top-5 text-sm border-white shadow-md min-w-[184px] ml-5 rounded-xl bg-white p-4 pointer-events-none">
                  {t('checkout.allTrades')}
                </p>
              </div>
            </Link>
          </div>
          <div className="pb-7 pt-5 flex flex-wrap border-b border-cyan-75">
            <span className="text-sm">{t('checkout.dividendAccruals')}</span>
            <span className="ml-auto">{data.corporateActionsCount}</span>
            <Link
              className="relative -top-[7px] -right-[2px] h-5"
              handleClick={(e) => e.preventDefault}>
              <ImageShow image={{ url: "question", label: "question" }} />
              <div className="relative -top-4 h-[12px] justify-center opacity-0 hover:opacity-100 w-[12px]">
                <p className="absolute -top-10 text-sm border-white shadow-md min-w-[184px] ml-5 rounded-xl bg-white p-4 pointer-events-none">
                  {t('checkout.allCorporateActions')}
                </p>
              </div>
            </Link>
          </div>
          
          {/* Block 2: Income Breakdown */}
          <div className="pt-7 pb-1 flex flex-wrap border-b border-gray-75">
            <span className="text-sm">{t('checkout.tradingProfit')}</span>
            <span className="ml-auto">{formatNumbers(tradingProfit)} {t('currency')}</span>
          </div>
          <div className="pt-5 flex flex-wrap border-b border-gray-75">
            <span className="text-sm">{t('checkout.dividendsIncome')}</span>
            <span className="ml-auto">{formatNumbers(dividendsIncome)} {t('currency')}</span>
          </div>
          <div className="pt-5 pb-7 flex flex-wrap border-b border-cyan-75">
            <span className="text-sm">{t('checkout.otherIncome')}</span>
            <span className="ml-auto">{formatNumbers(otherIncome)} {t('currency')}</span>
          </div>
          
          {/* Block 3: Taxes */}
          <div className="pt-7 pb-1 flex flex-wrap border-b border-gray-75">
            <span className="text-sm">{t('checkout.totalPdfo')}</span>
            <span className="ml-auto">{formatNumbers(totalPdfo)} {t('currency')}</span>
          </div>
          {
            env.ENABLE_MILITARY_TAX === "true" && (
              <div className="pt-5 flex flex-wrap border-b border-gray-75">
                <span className="text-sm">{t('checkout.totalMilitaryTax')}</span>
                <span className="ml-auto">
                  {formatNumbers(totalMilitaryTax)} {t('currency')}
                </span>
              </div>
            )
          }
          <div className="pt-6 flex text-lg flex-wrap">
            <span>{t('checkout.taxToPay')}</span>
            <span className="ml-auto">{formatNumbers(data.totalTax)} {t('currency')}</span>
          </div>
        </div>
        <div className="p-8 text-white text-sm flex flex-col">
          <p className="text-xs mb-4">{t('checkout.calculationCost')}</p>
          <div className="pb-1 flex text-sm flex-wrap border-b border-white">
            <span>{t('checkout.basePrice')}</span>
            <span className="ml-auto">${formatNumbers(data.basePrice)}</span>
          </div>
          <div className="mt-3 pb-1 flex items-center text-sm flex-wrap border-b border-white">
            <span>{t('checkout.tradesPrice')}</span>
            <span className="ml-2 text-xs">{t('checkout.tradesFormula')}</span>
            <span className="ml-auto">
              ${formatNumbers(data.additionalTradesPrice)}
            </span>
          </div>
          <div className="flex text-base flex-wrap mt-6 mb-1">
            <span>{t('checkout.referralBonuses')} ${bonus}</span>
            <span className="ml-auto flex items-center [&>*:last-child]:flex-row-reverse [&>*:last-child>*:first-child]:p-0">
              <Checkbox
                onChange={handleBonus}
                defaultChecked={bonusChecked}
                disabled={!bonus}
                label={
                  <Typography color="white" className="text-xs pr-3">
                    {t('checkout.useBonuses')}
                  </Typography>
                }
                ripple={false}
                className="checked:border-white rounded checked:rounded checked:bg-transporent
                    h-5 w-5 border-white bg-transparent transition-all hover:before:opacity-0"
                icon={
                  <ImageShow
                    className="max-w-none relative -top-[1px]"
                    image={{ url: "checked-white", label: "checked white" }}
                  />
                }
              />
            </span>
          </div>
          <div className="flex text-lg flex-wrap">
            <span>{t('checkout.totalPayment')}</span>
            <span className="ml-auto">
              ${formatNumbers(data.totalValueToPay)}
            </span>
          </div>
        </div>
      </div>
    );
  } else if (calculatePreliminaryData.status === "rejected") {
    content = (
      <div role="alert" className="rounded-[30px] bg-white px-8 py-7 text-sm text-black">
        {isFullYearReportRequired(calculatePreliminaryData.error) ? (
          <>
            <p>{t('checkout.fullYearReportRequired', { year })}</p>
            <Link to={uploadPath()} className="mt-3 inline-block text-cyan-650 underline">
              {t('checkout.uploadFullYearReport')}
            </Link>
          </>
        ) : (
          <p>{t('checkout.calculationFailed')}</p>
        )}
      </div>
    );
  } else {
    content = (
      <div className="rounded-[30px] text-black bg-gradient-to-r from-cyan-650 to-cyan-75"></div>
    );
  }

  if (bank === 'MONOBANK') {
    return (
        <div className="rounded-[60px] text-white bg-gradient-to-r from-cyan-650 to-cyan-75 px-[50px] pt-[35px] pb-[75px]">
          <h5 className="ml-5 mb-5 text-lg">{t('checkout.preliminaryCalculation')}</h5>
          <div className={clsx(
              "grid sm:gap-3 overflow-x-hidden items-start",
              bank === 'ALBPAY' && "xl:grid-cols-[500px_auto]",
              bank !== 'ALBPAY' && "xl:grid-cols-[650px_auto]",
          )}>
            {content}
            <div
                className="p-3 mt-8 xl:mt-0 justify-center text-black text-sm flex flex-col items-center h-full rounded-[30px] bg-white">
              {url && (<Monobank url={url} year={year}/>)}
            </div>
          </div>
        </div>
    );
  } else {
    return (
        <div
            className="rounded-[60px] text-white bg-gradient-to-r from-cyan-650 to-cyan-75 px-[50px] pt-[35px] pb-[75px]">
          <h5 className="ml-5 mb-5 text-lg">{t('checkout.preliminaryCalculation')}</h5>
          <div className={clsx(
              "grid sm:gap-3 overflow-x-hidden items-start",
              bank === 'ALBPAY' && "xl:grid-cols-[500px_auto]",
              bank !== 'ALBPAY' && "xl:grid-cols-[650px_auto]",
          )}>
            {content}
            <div
                className="p-3 mt-8 xl:mt-0 justify-center text-black text-sm flex flex-col items-center h-full rounded-[30px] bg-white">
              {url && <Fondy url={url}/>}
            </div>
          </div>
        </div>
    );
  }
}

export default Checkout;
